import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { login } from '../utils/authUtils';
import { LoginForm } from '../types/user';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 24px;
  width: 100%;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #202124;
  font-size: 0.875rem;
  font-weight: 500;
`;

const Required = styled.span`
  color: #d93025;
  font-size: 14px;
  line-height: 1;
`;

const InputGroup = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e1e3e6;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: white;
  
  &:focus {
    border-color: #1a73e8;
    box-shadow: 0 0 0 4px rgba(26, 115, 232, 0.1);
    outline: none;
  }
  
  &::placeholder {
    color: #80868b;
  }

  &:hover {
    border-color: #dadce0;
  }
`;

const RememberMe = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #5f6368;
  font-size: 0.875rem;
  cursor: pointer;
  user-select: none;
  margin-top: 4px;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  margin: 0;
  border: 2px solid #5f6368;
  border-radius: 2px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:checked {
    background-color: #1a73e8;
    border-color: #1a73e8;
  }
`;

const ForgotPassword = styled.a`
  color: #1a73e8;
  font-size: 0.875rem;
  text-decoration: none;
  margin-left: auto;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const RememberMeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 4px;
`;

const Button = styled.button<{ isLoading?: boolean }>`
  padding: 14px;
  background: ${props => props.isLoading ? '#ccc' : '#1a73e8'};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: ${props => props.isLoading ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  margin-top: 8px;
  position: relative;
  overflow: hidden;
  
  &:hover:not(:disabled) {
    background: #1557b0;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(26, 115, 232, 0.2);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  ${props => props.isLoading && `
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.2),
        transparent
      );
      animation: loading 1.5s infinite;
    }
  `}

  @keyframes loading {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(100%);
    }
  }
`;

const ErrorMessage = styled.div`
  color: #d93025;
  font-size: 0.875rem;
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  background: #fdeded;
  padding: 8px 12px;
  border-radius: 6px;
  animation: ${fadeIn} 0.3s ease-out;
  
  &::before {
    content: "⚠️";
  }
`;

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<LoginForm>({
    email: '',
    password: '',
  });
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      await login(formData);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : '登录失败');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <LoginContainer>
      <Form onSubmit={handleSubmit}>
        <FormField>
          <Label>
            邮箱
            <Required>*</Required>
          </Label>
          <InputGroup>
            <Input
              type="email"
              name="email"
              placeholder="请输入邮箱地址"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </InputGroup>
        </FormField>
        
        <FormField>
          <Label>
            密码
            <Required>*</Required>
          </Label>
          <InputGroup>
            <Input
              type="password"
              name="password"
              placeholder="请输入密码"
              value={formData.password}
              onChange={handleChange}
              required
            />
          </InputGroup>
          <RememberMeRow>
            <RememberMe>
              <Checkbox
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              记住我
            </RememberMe>
            <ForgotPassword href="/forgot-password">忘记密码？</ForgotPassword>
          </RememberMeRow>
        </FormField>
        
        <Button type="submit" isLoading={isLoading} disabled={isLoading}>
          {isLoading ? '登录中...' : '登录'}
        </Button>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </Form>
    </LoginContainer>
  );
};

export default Login; 